// ── Non-window drop/save commit ──
// Originally extracted verbatim from useFurnitureInteraction's onPointerUp (the
// `// ── Non-window furniture: normal save logic ──` branch). Variables that used to
// come from the enclosing closures are received through one explicit context object.
//
// Saving is local-first: the dropped item and everything it carries are written to
// the layout cache in ONE stageLayout() call (one undo step, no awaited request per
// item); ../state/layoutSync.ts sends the batch to the backend in the background.
//
// NOTE on the three drag maps (dragSurfaceMap / dragFootprintMap /
// dragSurfaceItemFootprintMap): they are reassigned only in onPointerUp's `finally`,
// i.e. strictly AFTER this function, so passing them by reference is safe.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { stageLayout, type LayoutPatch } from '../state/layoutSync';
import { isRugCategory } from '../../../domain/tileMath';
import { type PlacementMode, toPlacementMode, isSurface } from './placement';
import {
  checkSurfaceOverlap,
  footprintHasConflict,
  isFullyOnSurface,
  getFurnitureDepthComponents,
  getFurnitureDepth,
  getOccupiedBaseTile,
  type SurfaceTileMap,
} from '../../../domain/depthMath';
import { footprintOutOfRoom } from '../../../domain/footprintTolerance';
import type { CarriedItem } from './surfaceCarry';
import type { RenderFurniture } from '../../../domain/types';
import { asFurnitureMarker, asFurnitureSprite, liveFurnitureData } from '../render/pixiExtensions';

export interface DropCommitContext {
  // ── PIXI objects for the item being dropped (from onPointerUp) ──
  currentMarker: PIXI.Container;
  currentSprite: PIXI.Sprite | undefined;
  /** Only origX/origY are read — the spring-back position captured at drag start. */
  dragState: { origX: number; origY: number };
  /** Final marker position at drop time (read once in onPointerUp before this call). */
  newX: number;
  newY: number;

  // ── Per-sprite geometry, fixed for the whole interaction ──
  tileDimW: number | null | undefined;
  tileDimH: number | null | undefined;
  interactionIsRug: boolean;
  /** Live catalog read at call time so admin tile edits are picked up (see original). */
  getLiveCatalog: () => RenderFurniture['furniture_catalog'] | undefined;
  furniture: RenderFurniture;

  // ── Per-drag state built in onPointerDown ──
  dragSurfaceMap: SurfaceTileMap;
  dragFootprintMap: Map<string, number>;
  dragSurfaceItemFootprintMap: Map<string, number>;
  carriedItems: CarriedItem[];
  startIsInSurfaceArea: boolean;
  startTileX: number | null;
  startTileY: number | null;
  livePlacementMode: string | undefined;

  // ── Shared refs / services ──
  interiorMarkers: Map<number, PIXI.Container>;
  mapObjectId: number;
  queryClient: QueryClient;
  t: (key: string) => string;
  updatePreviewPosition: (instanceId: number, x: number, y: number) => void;
  updatePreviewPlacementMode: (instanceId: number, placement_mode: PlacementMode) => void;
}

/** Returns the resulting `dropHasConflict`. */
export async function commitNonWindowDrop(ctx: DropCommitContext): Promise<boolean> {
  const {
    currentMarker,
    currentSprite,
    dragState,
    newX,
    newY,
    tileDimW,
    tileDimH,
    interactionIsRug,
    getLiveCatalog,
    furniture,
    dragSurfaceMap,
    dragFootprintMap,
    dragSurfaceItemFootprintMap,
    carriedItems,
    startIsInSurfaceArea,
    startTileX,
    startTileY,
    livePlacementMode,
    interiorMarkers,
    mapObjectId,
    queryClient,
    updatePreviewPosition,
    updatePreviewPlacementMode,
  } = ctx;

  let dropHasConflict = false;

            // ── Non-window furniture: normal save logic ──
            const depthNow = getFurnitureDepthComponents(currentMarker, tileDimW, tileDimH, false);
            const isMirNow = currentSprite ? currentSprite.scale.x < 0 : false;

            // Determine surface placement: partial overlap = on surface, full fit = valid surface.
            // Rugs are always floor items — skip surface detection entirely for them.
            let isDroppedOnSurface = false;
            let fitsOnSurface = false;
            if (dragSurfaceMap.size > 0 && !interactionIsRug) {
              const baseNow = getOccupiedBaseTile(depthNow, isMirNow);
              const liveFpTilesOnDrop = getLiveCatalog()?.footprint_tiles;
              const fpCount = (liveFpTilesOnDrop?.length)
                ?? (depthNow.effectiveWidthTiles * depthNow.effectiveHeightTiles);
              const { isOnSurface } = checkSurfaceOverlap(
                baseNow.rawBaseTileX, baseNow.rawBaseTileY,
                baseNow.overlayWidthTiles, baseNow.overlayHeightTiles,
                dragSurfaceMap, undefined, fpCount,
              );
              isDroppedOnSurface = isOnSurface;
              if (isOnSurface) {
                const centerTileX = Math.round(depthNow.tileX);
                const centerTileY = Math.round(depthNow.tileY);
                fitsOnSurface = isFullyOnSurface(
                  liveFpTilesOnDrop,
                  centerTileX, centerTileY,
                  baseNow.rawBaseTileX, baseNow.rawBaseTileY,
                  baseNow.overlayWidthTiles, baseNow.overlayHeightTiles,
                  dragSurfaceMap, undefined, fpCount, isMirNow,
                );
              }
            }

            // Oversized for surface → spring back without saving
            if (isDroppedOnSurface && !fitsOnSurface) {
              if (currentMarker.position) {
                currentMarker.position.set(dragState.origX, dragState.origY);
                currentMarker.zIndex = getFurnitureDepth(currentMarker, tileDimW, tileDimH, interactionIsRug);
              }
              if (currentSprite) {
                currentSprite.tint = 0xFFFFFF;
                currentSprite.alpha = 1;
              }
              // Restore carried items to their original positions
              for (const carried of carriedItems) {
                if (carried.marker.position) {
                  carried.marker.position.set(carried.origX, carried.origY);
                }
              }
            } else {
              // Footprint conflicts — with the same edge tolerance as the drag preview
              // (footprintHasConflict), so what was green while dragging saves as green.
              const fpTilesNow = getLiveCatalog()?.footprint_tiles;
              if (!fitsOnSurface) {
                // On the floor: in a wall / past the floor edge counts too (rugs included,
                // judged by their base rectangle).
                dropHasConflict = interactionIsRug
                  ? footprintOutOfRoom(null, getFurnitureDepthComponents(currentMarker, tileDimW, tileDimH, true), isMirNow)
                  : footprintOutOfRoom(fpTilesNow, depthNow, isMirNow)
                    || (dragFootprintMap.size > 0 && footprintHasConflict(fpTilesNow, depthNow, isMirNow, dragFootprintMap));
              }
              if (!interactionIsRug && dragSurfaceItemFootprintMap.size > 0 && fitsOnSurface) {
                dropHasConflict = footprintHasConflict(fpTilesNow, depthNow, isMirNow, dragSurfaceItemFootprintMap);
              }
              if (currentSprite) {
                currentSprite.tint = dropHasConflict ? 0xFF4444 : 0xFFFFFF;
                currentSprite.alpha = dropHasConflict ? 0.5 : 1;
              }

              // Prevent accidental reclassification: if this floor item started within a surface
              // area AND didn't move to a new tile (user barely nudged it), keep it as 'floor'.
              // If the user intentionally dragged to a DIFFERENT tile, allow surface classification.
              // Items already classified as 'surface' are always re-evaluated normally.
              const endTileX = Math.round(depthNow.tileX);
              const endTileY = Math.round(depthNow.tileY);
              const movedToNewTile = startTileX !== null && startTileY !== null &&
                (endTileX !== startTileX || endTileY !== startTileY);
              const newPlacementMode: PlacementMode =
                fitsOnSurface && !(startIsInSurfaceArea && !isSurface(toPlacementMode(livePlacementMode)) && !movedToNewTile)
                  ? 'surface'
                  : 'floor';
              // Sync placement_mode (and optionally rug_z_order) on the PIXI sprite immediately.
              // The furniture render useEffect only runs after the next React render, so
              // the sprite's furnitureData is stale here. recomputeAllZIndexes() reads
              // both fields — sync them now so z-order is correct before the next render.
              // SceneNode is the domain surface; at runtime these are PIXI containers.
              const syncFurnitureData = (marker: import('../../../domain/sceneNode').SceneNode, patch: Partial<RenderFurniture>) => {
                const fm = asFurnitureMarker(marker);
                const sp = asFurnitureSprite(marker.children[0] as object | undefined);
                if (sp?.furnitureData) {
                  sp.furnitureData = { ...sp.furnitureData, ...patch };
                }
                if (fm?.furnitureData) {
                  fm.furnitureData = { ...fm.furnitureData, ...patch };
                }
              };

              if (furniture.is_preview) {
                const pInstId3 = -(furniture.id + 100000);
                updatePreviewPosition(pInstId3, newX, newY);
                updatePreviewPlacementMode(pInstId3, newPlacementMode);
                syncFurnitureData(currentMarker, { placement_mode: newPlacementMode });
                // Save all carried items (purchased or preview) to their new positions.
                // Without this loop they would revert when the React render fires.
                const carriedPatches: LayoutPatch[] = [];
                for (const carried of carriedItems) {
                  const cx = carried.marker.position.x;
                  const cy = carried.marker.position.y;
                  const carriedFData = liveFurnitureData(
                    asFurnitureSprite(carried.marker.children[0] as object | undefined),
                    asFurnitureMarker(carried.marker),
                  );
                  if (carriedFData?.is_preview) {
                    const carriedInstId = -(carriedFData.id + 100000);
                    updatePreviewPosition(carriedInstId, cx, cy);
                    updatePreviewPlacementMode(carriedInstId, 'surface');
                  } else {
                    carriedPatches.push({ id: carried.furnitureId, x: cx, y: cy, placement_mode: 'surface' });
                  }
                  syncFurnitureData(carried.marker, { placement_mode: 'surface' });
                }
                stageLayout(queryClient, mapObjectId, carriedPatches);
              } else {
                // For rugs: determine the new rug_z_order so the dragged rug renders on top
                // of any rug it overlaps with. We find the current max rug_z_order among all
                // placed rugs (excluding self) and assign max+1. This way the last-moved rug
                // always wins depth battles with other overlapping rugs.
                let newRugZOrder: number | undefined;
                if (interactionIsRug) {
                  let maxOrder = 0;
                  for (const [mId, m] of interiorMarkers.entries()) {
                    if (mId === furniture.id) continue;
                    const mData = liveFurnitureData(
                      asFurnitureSprite(m.children[0] as object | undefined),
                      asFurnitureMarker(m),
                    );
                    if (mData && isRugCategory(mData.furniture_catalog?.category)) {
                      maxOrder = Math.max(maxOrder, mData.rug_z_order ?? 0);
                    }
                  }
                  newRugZOrder = maxOrder + 1;
                }
                // The dropped item and everything it carries change in ONE local
                // layout write — one undo step, no network wait (layoutSync syncs it).
                const patches: LayoutPatch[] = [{
                  id: furniture.id,
                  x: newX,
                  y: newY,
                  placement_mode: newPlacementMode,
                  ...(newRugZOrder !== undefined ? { rug_z_order: newRugZOrder } : {}),
                }];
                const dataPatch: Partial<RenderFurniture> = { placement_mode: newPlacementMode };
                if (newRugZOrder !== undefined) dataPatch.rug_z_order = newRugZOrder;
                syncFurnitureData(currentMarker, dataPatch);
                // Carried items stay on the surface at their new positions.
                for (const carried of carriedItems) {
                  const cx = carried.marker.position.x;
                  const cy = carried.marker.position.y;
                  // Carried item may be a preview item (negative id)
                  const carriedFData = liveFurnitureData(
                    asFurnitureSprite(carried.marker.children[0] as object | undefined),
                    asFurnitureMarker(carried.marker),
                  );
                  if (carriedFData?.is_preview) {
                    const carriedInstId2 = -(carriedFData.id + 100000);
                    updatePreviewPosition(carriedInstId2, cx, cy);
                    updatePreviewPlacementMode(carriedInstId2, 'surface');
                  } else {
                    patches.push({ id: carried.furnitureId, x: cx, y: cy, placement_mode: 'surface' });
                  }
                  syncFurnitureData(carried.marker, { placement_mode: 'surface' });
                }
                stageLayout(queryClient, mapObjectId, patches);
              }
            }

  return dropHasConflict;
}
